import React, { useState, useEffect, useCallback } from "react";
import CoachingDashboardSkeleton from "../../components/layout/CoachingDashboardSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
    Users, Check, ChevronRight, BarChart3,
    ClipboardList, Activity, Clock, PieChart,
    GraduationCap, Sparkles, MessageSquare,
    UserCheck, Settings, Star, UserCircle
} from "lucide-react";

export default function CoachingDashboard() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [mentees, setMentees] = useState([]);
    const [requests, setRequests] = useState([]);
    const [dashStats, setDashStats] = useState(null);
    const [recentFeedback, setRecentFeedback] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [menteesRes, reqRes, statsRes, fbRes] = await Promise.all([
                api.get("/coach/my-mentees"),
                api.get("/coach/requests"),
                api.get("/coach/dashboard-stats"),
                api.get("/coach/feedback/all"),
            ]);
            setMentees(menteesRes.data.mentees || []);
            setRequests(reqRes.data.requests || []);
            setDashStats(statsRes.data);
            setRecentFeedback((fbRes.data.feedback || []).slice(0, 5));
        } catch {
            setDashStats(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const avatarColors = [
        "from-blue-500 to-indigo-600", "from-primary-500 to-teal-600",
        "from-violet-500 to-purple-600", "from-rose-500 to-pink-600",
        "from-amber-500 to-orange-600", "from-cyan-500 to-sky-600",
    ];
    const getAvatarColor = (id) => avatarColors[(id || 0) % avatarColors.length];

    if (loading) {
        return <CoachingDashboardSkeleton isDarkMode={isDarkMode} />;
    }

    const quickLinks = [
        { label: "My Students", desc: `${mentees.length} active`, icon: Users, path: "/coaching/students", color: "from-primary-500 to-teal-500", light: "bg-primary-50 text-primary-600" },
        { label: "My Prospects", desc: "Manage leads", icon: UserCheck, path: "/coaching/prospects", color: "from-blue-500 to-indigo-500", light: "bg-blue-50 text-blue-600" },
        { label: "Reporting", desc: "Feedback & stats", icon: BarChart3, path: "/coaching/reporting", color: "from-orange-500 to-amber-500", light: "bg-orange-50 text-orange-600" },
        { label: "My Program", desc: "Suggestions & Defaults", icon: Settings, path: "/coaching/preferences", color: "from-purple-500 to-violet-500", light: "bg-purple-50 text-purple-600" },
        { label: "My Profile", desc: "Profile settings", icon: UserCircle, path: "/coaching/profile", color: "from-cyan-500 to-sky-500", light: "bg-cyan-50 text-cyan-600" },
    ];

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="rounded-2xl overflow-hidden mb-8 shadow-sm">
                    <div className={`relative px-6 py-8 ${isDarkMode ? "bg-gradient-to-r from-primary-900 via-teal-900 to-cyan-900" : "bg-gradient-to-r from-primary-600 via-teal-600 to-cyan-600"}`}>
                        <Sparkles className="absolute top-4 right-6 text-white/20" size={40} />
                        <div className="relative flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-lg border border-white/30">
                                <GraduationCap size={28} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-extrabold tracking-tight text-white">Coaching Dashboard</h1>
                                <p className="text-sm text-white/70">Overview of your coaching activity</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                {dashStats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                        {[
                            { label: "Students", value: dashStats.totalMentees, icon: Users, bg: "from-blue-500 to-indigo-500", light: "bg-blue-50 text-blue-600" },
                            { label: "Pending", value: dashStats.pendingRequests, icon: Clock, bg: "from-amber-500 to-orange-500", light: "bg-amber-50 text-amber-600" },
                            { label: "Total Tasks", value: dashStats.totalTasks, icon: ClipboardList, bg: "from-indigo-500 to-violet-500", light: "bg-indigo-50 text-indigo-600" },
                            { label: "Completed", value: dashStats.completedTasks, icon: Check, bg: "from-primary-500 to-teal-500", light: "bg-primary-50 text-primary-600" },
                            { label: "In Progress", value: dashStats.inProgressTasks, icon: Activity, bg: "from-orange-500 to-rose-500", light: "bg-orange-50 text-orange-600" },
                            { label: "Avg Completion", value: `${dashStats.avgCompletionRate}%`, icon: PieChart, bg: "from-purple-500 to-pink-500", light: "bg-purple-50 text-purple-600" },
                        ].map((s) => (
                            <div key={s.label} className={`rounded-2xl border p-4 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${isDarkMode ? `bg-gradient-to-br ${s.bg} shadow-lg` : s.light}`}>
                                    <s.icon size={18} className={isDarkMode ? "text-white" : ""} />
                                </div>
                                <p className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{s.value}</p>
                                <p className={`text-xs font-medium mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{s.label}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Quick Links */}
                <h2 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Quick Navigation</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
                    {quickLinks.map((link) => (
                        <button key={link.path} onClick={() => navigate(link.path)}
                            className={`group rounded-2xl border p-4 text-left transition-all hover:shadow-lg ${isDarkMode ? "bg-slate-800 border-slate-700 hover:border-slate-600" : "bg-white border-slate-200 hover:border-primary-300 shadow-sm hover:shadow-primary-100/50"}`}>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${isDarkMode ? `bg-gradient-to-br ${link.color} shadow-lg` : link.light}`}>
                                <link.icon size={18} className={isDarkMode ? "text-white" : ""} />
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{link.label}</p>
                            <p className={`text-xs mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{link.desc}</p>
                        </button>
                    ))}
                </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Recent Students */}
                    <div className={`rounded-2xl border p-5 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className={`text-base font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                <Users size={16} className="inline-block mr-2 -mt-0.5" /> Students
                            </h2>
                            <button onClick={() => navigate("/coaching/students")} className={`text-xs font-semibold flex items-center gap-1 ${isDarkMode ? "text-primary-400 hover:text-primary-300" : "text-primary-600 hover:text-primary-700"}`}>
                                View All <ChevronRight size={14} />
                            </button>
                        </div>
                        {mentees.length === 0 ? (
                            <div className="text-center py-6">
                                <Users className={`mx-auto mb-2 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} size={32} />
                                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No students yet</p>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {mentees.slice(0, 5).map((m) => (
                                    <div key={m.ClientID} onClick={() => navigate("/coaching/students")} role="button" tabIndex={0}
                                        className={`group flex items-center gap-3 rounded-xl p-2.5 cursor-pointer transition-all ${isDarkMode ? "hover:bg-slate-700/60" : "hover:bg-slate-50"}`}>
                                        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${getAvatarColor(m.ClientID)} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                                            {m.FirstName?.[0]}{m.LastName?.[0]}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{m.FirstName} {m.LastName}</p>
                                            <p className={`text-xs truncate ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{m.EmailAddress}</p>
                                        </div>
                                        <ChevronRight size={14} className={`transition-transform group-hover:translate-x-0.5 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                                    </div>
                                ))}
                                {mentees.length > 5 && (
                                    <p className={`text-xs text-center pt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>+{mentees.length - 5} more</p>
                                )}
                            </div>
                        )}

                        {/* Pending Requests */}
                        {requests.length > 0 && (
                            <div className={`mt-4 pt-4 border-t ${isDarkMode ? "border-slate-700" : "border-slate-100"}`}>
                                <div className="flex items-center justify-between mb-2.5">
                                    <h3 className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? "text-amber-400" : "text-amber-600"}`}>
                                        <Clock size={12} className="inline-block mr-1 -mt-0.5" /> {requests.length} Pending
                                    </h3>
                                    <button onClick={() => navigate("/coaching/students")} className={`text-xs font-semibold ${isDarkMode ? "text-primary-400 hover:text-primary-300" : "text-primary-600 hover:text-primary-700"}`}>
                                        Manage
                                    </button>
                                </div>
                                {requests.slice(0, 3).map((r) => (
                                    <div key={r.RequestID} className={`flex items-center gap-3 rounded-lg p-2 mb-1.5 ${isDarkMode ? "bg-amber-900/10" : "bg-amber-50/70"}`}>
                                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${getAvatarColor(r.RequestID)} flex items-center justify-center text-white font-bold text-[10px]`}>
                                            {r.FirstName?.[0]}{r.LastName?.[0]}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-xs font-semibold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{r.FirstName} {r.LastName}</p>
                                        </div>
                                        <span className={`text-[10px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{r.RequestDate}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Recent Feedback */}
                    <div className={`rounded-2xl border p-5 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className={`text-base font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                <MessageSquare size={16} className="inline-block mr-2 -mt-0.5" /> Recent Feedback
                            </h2>
                            <button onClick={() => navigate("/coaching/reporting")} className={`text-xs font-semibold flex items-center gap-1 ${isDarkMode ? "text-primary-400 hover:text-primary-300" : "text-primary-600 hover:text-primary-700"}`}>
                                View All <ChevronRight size={14} />
                            </button>
                        </div>
                        {recentFeedback.length === 0 ? (
                            <div className="text-center py-6">
                                <MessageSquare className={`mx-auto mb-2 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} size={32} />
                                <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No feedback yet</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {recentFeedback.map((f) => (
                                    <div key={f.FeedbackID} className={`rounded-xl p-3 ${isDarkMode ? "hover:bg-slate-700/60" : "hover:bg-slate-50"} transition-all`}>
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getAvatarColor(f.FeedbackID)} flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0 mt-0.5`}>
                                                {f.FirstName?.[0]}{f.LastName?.[0]}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{f.FirstName} {f.LastName}</span>
                                                    {f.Rating && (
                                                        <div className="flex items-center gap-0.5">
                                                            {[1, 2, 3, 4, 5].map((s) => (
                                                                <Star key={s} size={10} className={s <= f.Rating ? "text-amber-400 fill-amber-400" : isDarkMode ? "text-slate-600" : "text-slate-300"} />
                                                            ))}
                                                        </div>
                                                    )}
                                                    <span className={`text-[10px] ml-auto flex-shrink-0 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{f.CreatedOn}</span>
                                                </div>
                                                <p className={`text-xs line-clamp-2 leading-relaxed ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>{f.FeedbackText}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
